import {isNotNil} from 'ramda';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  carPassApiStateSchema,
  ConfirmCarPassRequestApiArg,
  ConfirmCarPassRequestApiResponse,
  CorrectCarPassRequestApiArg,
  CorrectCarPassRequestApiResponse,
  CreateCarPassRequestApiArg,
  CreateCarPassRequestApiResponse,
  createCarPassRequestResponseBodySchema,
  GetCarPassRequestApiArg,
  GetCarPassRequestApiResponse,
  getCarPassRequestResponseBodySchema,
  GetInProgressCarPassRequestByVehicleApiArg,
  GetInProgressCarPassRequestByVehicleApiResponse,
  GetLatestCarPassRequestByVehicleApiArg,
  GetLatestCarPassRequestByVehicleApiResponse,
  GetReadyCarPassRequestByVehicleApiArg,
  GetReadyCarPassRequestByVehicleApiResponse,
} from './types';

const carPassApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getCarPassRequest: build.query<GetCarPassRequestApiResponse, GetCarPassRequestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/car-pass/${queryArg.requestId}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'CarpassRequest', id: queryArg.requestId},
        {type: 'CarpassRequest', id: `vehicle-${queryArg.vehicleId}`},
      ],
      extraOptions: {
        responseSchema: getCarPassRequestResponseBodySchema,
      },
    }),
    createCarPassRequest: build.mutation<
      CreateCarPassRequestApiResponse,
      CreateCarPassRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/car-pass`,
        method: 'POST',
        // BE T20-124197: the create endpoint accepts an optional
        // `mileageReadoutDate`, but the BE body resolver always JSON-decodes the
        // raw request content. An empty body is invalid JSON and is rejected
        // with "Invalid request body (JSON)", so always send a JSON object and
        // omit only the field (BE then defaults to today's date).
        body: isNotNil(queryArg.mileageReadoutDate)
          ? {mileageReadoutDate: queryArg.mileageReadoutDate}
          : {},
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: CreateCarPassRequestApiArg) => [
        {type: 'CarpassRequest', id: `vehicle-${queryArg.vehicleId}`},
        // TODO Q-BE-1: once BE exposes latestCarPassRequestId on the vehicle entity
        // (or a list endpoint), invalidate the Vehicle tag too so the widget
        // can rediscover the request after a page reload. Until then, the FE
        // falls back to localStorage via carPassRequestStorage.
        {type: 'Vehicle'},
      ]),
      extraOptions: {
        responseSchema: createCarPassRequestResponseBodySchema,
      },
    }),
    confirmCarPassRequest: build.mutation<
      ConfirmCarPassRequestApiResponse,
      ConfirmCarPassRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/car-pass/${queryArg.requestId}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: ConfirmCarPassRequestApiArg) => [
        {type: 'CarpassRequest', id: queryArg.requestId},
        {type: 'CarpassRequest', id: `vehicle-${queryArg.vehicleId}`},
      ]),
    }),
    correctCarPassRequest: build.mutation<
      CorrectCarPassRequestApiResponse,
      CorrectCarPassRequestApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/car-pass/${queryArg.requestId}/correct`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess((result, queryArg: CorrectCarPassRequestApiArg) => [
        {type: 'CarpassRequest', id: queryArg.requestId},
        {type: 'CarpassRequest', id: `vehicle-${queryArg.vehicleId}`},
        // Vehicle tag — the correct flow PATCHes vehicle mileage first, so
        // vehicle data will have been updated by the standard PATCH endpoint
        // separately; this is an additional safety net.
        {type: 'Vehicle'},
      ]),
    }),
    // TODO T20-127XXX: remove once `getLatestCarPassRequestByVehicle` is
    // verified on prod. No longer consumed by the widget — the FE switched to
    // the single `/car-pass` (latest) endpoint, which returns the latest
    // request regardless of state. Kept temporarily to avoid premature deletion
    // until the new endpoint is observed working end-to-end.
    getInProgressCarPassRequestByVehicle: build.query<
      GetInProgressCarPassRequestByVehicleApiResponse,
      GetInProgressCarPassRequestByVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/car-pass/in-progress`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'CarpassRequest', id: `vehicle-${queryArg.vehicleId}`},
      ],
      extraOptions: {
        responseSchema: getCarPassRequestResponseBodySchema,
      },
    }),
    // TODO T20-127XXX: remove once `getLatestCarPassRequestByVehicle` is
    // verified on prod. See note on `getInProgressCarPassRequestByVehicle`.
    getReadyCarPassRequestByVehicle: build.query<
      GetReadyCarPassRequestByVehicleApiResponse,
      GetReadyCarPassRequestByVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/car-pass/ready`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'CarpassRequest', id: `vehicle-${queryArg.vehicleId}`},
      ],
      extraOptions: {
        responseSchema: getCarPassRequestResponseBodySchema,
      },
    }),
    getLatestCarPassRequestByVehicle: build.query<
      GetLatestCarPassRequestByVehicleApiResponse,
      GetLatestCarPassRequestByVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/car-pass`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'CarpassRequest', id: `vehicle-${queryArg.vehicleId}`},
      ],
      async onQueryStarted({vehicleId}, {dispatch, getCacheEntry, queryFulfilled}) {
        const previousState = getCacheEntry().data?.state;
        try {
          const {data} = await queryFulfilled;
          const isReady =
            isNotNil(previousState) &&
            previousState !== carPassApiStateSchema.enum.READY &&
            data.state === carPassApiStateSchema.enum.READY;
          if (isReady) {
            dispatch(omneticApi.util.invalidateTags([{type: 'documentsCount', id: vehicleId}]));
          }
        } catch {
          // Request failed — nothing to reconcile with the documents count.
        }
      },
      extraOptions: {
        responseSchema: getCarPassRequestResponseBodySchema,
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCarPassRequestQuery,
  useGetInProgressCarPassRequestByVehicleQuery,
  useGetLatestCarPassRequestByVehicleQuery,
  useGetReadyCarPassRequestByVehicleQuery,
  useCreateCarPassRequestMutation,
  useConfirmCarPassRequestMutation,
  useCorrectCarPassRequestMutation,
} = carPassApi;
